import useUserStore from '~/store/modules/user'
import { storeToRefs } from 'pinia'

/** 判定所需的最小用户结构（不直接用 IUserInfo，便于纯函数脱离 store 独立测试） */
export interface CancelEligibilityUser {
  vipLevel?: number | null
  validWordsForBag?: number | null
}

/** 判定所需的最小任务项结构（uploadItem 等非 FileItem 场景也可复用，不直接用 FileItem） */
export interface CancelEligibilityItem {
  operateStatus?: number | null
  userId?: string | number | null
  freeTag?: number | null
  fileType?: string | null
  bizTeamId?: number | null
}

/**
 * 判断任务是否可以显示"取消"按钮，逐条对齐后端 assertCancelSupported。
 * 纯函数，无副作用，便于单测。
 */
export function isCancelEligible(
  item: CancelEligibilityItem,
  user: CancelEligibilityUser | null | undefined,
): boolean {
  // 对应后端检查 1：任务必须处于进行中（operateStatus === 0）
  if (item.operateStatus !== 0) {
    return false
  }

  // 对应后端检查 2：游客任务（userId 为空）不可取消
  if (item.userId === null || item.userId === undefined || item.userId === '') {
    return false
  }

  // 对应后端检查 3：免费任务（freeTag === 1）不可取消
  if (item.freeTag === 1) {
    return false
  }

  // 对应后端检查 4：仅 PDF 文件支持取消
  if ((item.fileType ?? '').toLowerCase() !== 'pdf') {
    return false
  }

  // 对应后端检查 5：权益三选一——vip、字符包余量、企业任务（bizTeamId 非空）任一满足即可放行。
  // 注意：企业 web 与 C 端逻辑相反，企业任务（bizTeamId 非空）在这里是放行条件之一，而不是排除条件。
  const hasVip = !!user?.vipLevel && user.vipLevel > 0
  const hasWordsForBag = !!user?.validWordsForBag && user.validWordsForBag > 0
  const hasBizTeam = item.bizTeamId !== null && item.bizTeamId !== undefined
  if (!hasVip && !hasWordsForBag && !hasBizTeam) {
    return false
  }

  return true
}

/** 组件用：绑定当前登录用户，返回 canCancel 判定函数 */
export function useCancelEligibility() {
  const userStore = useUserStore()
  const { userInfo } = storeToRefs(userStore)

  const canCancel = (item: CancelEligibilityItem): boolean => {
    return isCancelEligible(item, userInfo.value)
  }

  return {
    canCancel,
  }
}
